import React, { useState, useEffect } from 'react';
import api from '../api';

export default function AccessControl() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [changes, setChanges] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await api.get('/access-control');
      setData(res.data);
    } catch {} finally { setLoading(false); }
  };

  const toggle = (group, permission, role) => {
    const key = `${permission}__${role}`;
    setChanges(prev => {
      const current = prev[key];
      const groupData = data?.data?.[group] || [];
      const item = groupData.find(p => p.permission === permission);
      const currentVal = current !== undefined ? current : (item?.[role] || false);
      return { ...prev, [key]: !currentVal };
    });
  };

  const getValue = (group, permission, role) => {
    const key = `${permission}__${role}`;
    if (changes[key] !== undefined) return changes[key];
    const groupData = data?.data?.[group] || [];
    const item = groupData.find(p => p.permission === permission);
    return item?.[role] || false;
  };

  const save = async () => {
    setSaving(true);
    try {
      const permissions = Object.entries(changes).map(([key, granted]) => {
        const [permission, role] = key.split('__');
        return { role, permission, granted };
      });
      await api.put('/access-control', { permissions });
      setMsg({ ok: true, text: 'Permissions updated successfully.' });
      setChanges({});
      load();
    } catch {
      setMsg({ ok: false, text: 'Failed to update permissions.' });
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm('Reset all permissions to system defaults?')) return;
    setSaving(true);
    try {
      await api.post('/access-control/reset');
      setMsg({ ok: true, text: 'Permissions reset to defaults.' });
      setChanges({});
      load();
    } catch {} finally { setSaving(false); }
  };

  if (loading) return <div className="page-wrap"><div className="text-muted" style={{ textAlign:'center', padding:40 }}>Loading access control...</div></div>;

  const groups = Object.keys(data?.data || {});
  const roles = data?.roles || ['manager', 'member', 'client'];
  const pendingCount = Object.keys(changes).length;

  const roleColors = { manager:'#6c58f0', member:'#3b82f6', client:'#10b981' };

  return (
    <div className="page-wrap">
      {/* Header */}
      <div className="card" style={{ marginBottom:16 }}>
        <div className="card-header" style={{ flexWrap:'wrap', gap:12 }}>
          <div style={{ flex:1 }}>
            <div className="card-title">🔐 Access Control</div>
            <div className="text-sm text-muted">Configure what each role can see and do. Owners always have full access.</div>
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
            {pendingCount > 0 && (
              <span style={{ background:'#f59e0b18', border:'1px solid #f59e0b44', color:'#f59e0b', padding:'4px 12px', borderRadius:20, fontSize:'0.75rem', fontWeight:600 }}>
                {pendingCount} unsaved change{pendingCount > 1 ? 's' : ''}
              </span>
            )}
            <button className="btn btn-secondary" onClick={reset} disabled={saving}>Reset to Defaults</button>
            <button className="btn btn-primary" onClick={save} disabled={saving || pendingCount === 0}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {msg && (
        <div style={{
          padding:'10px 14px', borderRadius:8, fontSize:'0.82rem', marginBottom:14,
          background: msg.ok ? '#f0fdf4' : '#fef2f2',
          border: `1px solid ${msg.ok ? '#22c55e' : '#ef4444'}`,
          color: msg.ok ? '#16a34a' : '#b91c1c',
        }}>{msg.text}</div>
      )}

      <div style={{ background:'var(--bg-alt,#f8fafc)', border:'1px solid var(--border)', borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:'0.82rem', color:'var(--text-muted)' }}>
        🔒 <strong style={{ color:'var(--text)' }}>Owner</strong> always has all permissions and cannot be restricted. Changes here only affect Manager, Member, and Client roles.
      </div>

      {groups.map(group => {
        const items = data.data[group] || [];
        return (
          <div key={group} className="card" style={{ marginBottom:16 }}>
            <div style={{ padding:'10px 16px', borderBottom:'1px solid var(--border)', background:'var(--bg-alt,#f8fafc)' }}>
              <span className="fw-600 text-sm" style={{ textTransform:'uppercase', letterSpacing:'0.04em' }}>{group}</span>
            </div>
            <div style={{ overflow:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.82rem' }}>
                <thead>
                  <tr style={{ borderBottom:'1px solid var(--border)' }}>
                    <th style={thStyle}>Permission</th>
                    {roles.map(role => (
                      <th key={role} style={{...thStyle, textAlign:'center', width:120}}>
                        <span style={{ display:'inline-block', padding:'3px 12px', borderRadius:20, fontSize:'0.72rem', fontWeight:600, background: roleColors[role]+'18', color: roleColors[role] }}>
                          {role}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => (
                    <tr key={item.permission} style={{ borderBottom:'1px solid var(--border)' }}>
                      <td style={tdStyle}>
                        <div className="fw-600 text-sm">{item.label}</div>
                        <div className="text-xs text-muted" style={{ fontFamily:'monospace', marginTop:1 }}>{item.permission}</div>
                      </td>
                      {roles.map(role => {
                        const val = getValue(group, item.permission, role);
                        const changed = changes[`${item.permission}__${role}`] !== undefined;
                        return (
                          <td key={role} style={{...tdStyle, textAlign:'center'}}>
                            <button
                              onClick={() => toggle(group, item.permission, role)}
                              style={{
                                width:32, height:32, borderRadius:8, fontSize:'0.82rem', fontWeight:700, cursor:'pointer',
                                transition:'all 0.15s',
                                background: val ? roleColors[role] : 'var(--bg-alt,#f1f5f9)',
                                color: val ? '#fff' : 'var(--text-muted)',
                                border: changed ? '2px solid #f59e0b' : '2px solid var(--border)',
                              }}
                            >
                              {val ? '✓' : '✕'}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const thStyle = {
  textAlign:'left', padding:'10px 14px', fontSize:'0.75rem',
  fontWeight:600, color:'var(--text-muted)', textTransform:'uppercase',
  letterSpacing:'0.04em',
};
const tdStyle = {
  padding:'10px 14px', verticalAlign:'middle',
};
